import { ColDef, ColGroupDef, IAggFuncParams, ITooltipParams, ValueGetterParams } from 'ag-grid-community';
import { percentageFormatter } from 'src/util/grid/cellFormatter';

import { formatMonthHeader } from '../../utils';
import {
    createEligibilityCellClassRules,
    createMssAggFunc,
    createValueGetter,
    isEligibleForHighlighting,
    numberFormatter
} from '../MssViewPivot/mssPivotColDefs';

// Util function to create column definition for a specific metric
const createMetricColumn = (
    month: string,
    mode: 'actm' | 'standard',
    field: 'VendorPlays' | 'TotalPlays' | 'Mss',
    headerName: string,
    minWidth: number,
    isAntiDilutionMode: boolean
): ColDef => {
    const isMssColumn = field === 'Mss';
    const isActmColumn = mode === 'actm';

    return {
        field: `${month}_${mode.toLowerCase()}${field}`,
        headerName,
        filter: 'agNumberColumnFilter',
        flex: 1,
        minWidth,
        sortable: true,
        valueFormatter: isMssColumn ? percentageFormatter : numberFormatter,
        valueGetter: createValueGetter(month, mode, field, isAntiDilutionMode),
        cellClassRules: createEligibilityCellClassRules(month, mode),
        aggFunc: isMssColumn ? createMssAggFunc(month, mode, isAntiDilutionMode) : 'sum',
        headerTooltip: isActmColumn ? 'ACTM: Artist Centric Threshold Model' : '',
        tooltipValueGetter: (params: ITooltipParams) => {
            return isEligibleForHighlighting(params, month, mode) ? 'Actuals' : '';
        }
    };
};

/**
 * Creates dynamic column definitions for UMG MSS view
 * Territory, then dynamic month column groups with 12 sub-columns each (ACTM + Standard) * (Combined + Music-only)
 * Optimized for UMG data only
 * @param availableMonths - Array of month strings (YYYYMM format)
 */
export const createUmgMssColDefs = (availableMonths: string[]): (ColDef | ColGroupDef)[] => {
    const baseColumns: ColDef[] = [
        {
            field: 'vendor',
            headerName: 'Vendor',
            flex: 2,
            minWidth: 180,
            sortable: true,
            rowGroup: true,
            hide: true
        }
    ];

    // Create dynamic month column groups for UMG data (ACTM + Standard)
    const monthColumnGroups: ColGroupDef[] = availableMonths.map((month) => ({
        headerName: formatMonthHeader(month),
        children: [
            // ACTM Columns (isArtistCentric=true)
            createMetricColumn(month, 'actm', 'VendorPlays', 'ACTM Music-only Vendor Plays', 130, true),
            createMetricColumn(month, 'actm', 'TotalPlays', 'ACTM Music-only Total Plays', 130, true),
            createMetricColumn(month, 'actm', 'Mss', 'ACTM Music-only MSS %', 110, true),
            createMetricColumn(month, 'actm', 'VendorPlays', 'ACTM Combined Vendor Plays', 130, false),
            createMetricColumn(month, 'actm', 'TotalPlays', 'ACTM Combined Total Plays', 130, false),
            createMetricColumn(month, 'actm', 'Mss', 'ACTM Combined MSS %', 110, false),
            {
                field: `${month}_actm_antidilution_multiplier`,
                headerName: 'ACTM Anti Dilution Multiplier',
                filter: 'agNumberColumnFilter',
                flex: 1,
                minWidth: 110,
                sortable: true,
                valueFormatter: numberFormatter,
                valueGetter: (params: ValueGetterParams) => {
                    const value = params.data?.[`${month}_actmMusicOnlyMss`] / params.data?.[`${month}_actmMss`];
                    return value != null ? value : 0;
                },
                cellClassRules: createEligibilityCellClassRules(month, 'actm'),
                aggFunc: (params: IAggFuncParams) => {
                    return '';
                },
                headerTooltip: 'ACTM: Artist Centric Threshold Model',
                tooltipValueGetter: (params: ITooltipParams) => {
                    return params.data?.[`${month}_eligibleMode`] === 'actm' ? 'Actuals' : '';
                }
            },
            // Standard Columns (isArtistCentric=false)
            createMetricColumn(month, 'standard', 'VendorPlays', 'Standard Music-only Vendor Plays', 140, true),
            createMetricColumn(month, 'standard', 'TotalPlays', 'Standard Music-only Total Plays', 140, true),
            createMetricColumn(month, 'standard', 'Mss', 'Standard Music-only MSS %', 120, true),
            createMetricColumn(month, 'standard', 'VendorPlays', 'Standard Combined Vendor Plays', 140, false),
            createMetricColumn(month, 'standard', 'TotalPlays', 'Standard Combined Total Plays', 140, false),
            createMetricColumn(month, 'standard', 'Mss', 'Standard Combined MSS %', 120, false),
            {
                field: `${month}_standard_antidilution_multiplier`,
                headerName: 'Standard Anti Dilution Multiplier',
                filter: 'agNumberColumnFilter',
                flex: 1,
                minWidth: 110,
                sortable: true,
                valueFormatter: numberFormatter,
                valueGetter: (params: ValueGetterParams) => {
                    const value =
                        params.data?.[`${month}_standardMusicOnlyMss`] / params.data?.[`${month}_standardMss`];
                    return value != null ? value : 0;
                },
                cellClassRules: createEligibilityCellClassRules(month, 'standard'),
                aggFunc: (params: IAggFuncParams) => {
                    return '';
                }
            }
        ]
    }));

    return [...baseColumns, ...monthColumnGroups];
};

/**
 * Static column definitions for when no UMG data is available
 */
export const umgMssColDefsEmpty: (ColDef | ColGroupDef)[] = [
    {
        field: 'territory',
        headerName: 'Territory',
        filter: 'agSetColumnFilter',
        flex: 1,
        minWidth: 150,
        sortable: true
    },
    {
        field: 'vendor',
        headerName: 'Vendor',
        filter: 'agSetColumnFilter',
        flex: 2,
        minWidth: 180,
        sortable: true
    }
];
